import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ShoppingBag, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCart } from '@/context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollPaymentStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('pending');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(data);
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        attempts += 1;
        setTimeout(pollPaymentStatus, pollInterval);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollPaymentStatus();
  }, [sessionId, clearCart]);

  return (
    <div className="min-h-screen py-24 md:py-32 flex items-center" data-testid="checkout-success-page">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto"
        >
          <Card className="bg-card border-border/50 overflow-hidden">
            <CardContent className="p-8 text-center">
              {status === 'loading' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-secondary animate-spin" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Processing Payment...
                  </h1>
                  <p className="text-muted-foreground">
                    Please wait while we confirm your order.
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', duration: 0.5 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"
                  >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }} data-testid="success-title">
                    Payment Successful!
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Thank you for your purchase. Your order has been confirmed.
                  </p>
                  
                  {paymentDetails && (
                    <div className="bg-muted/50 rounded-xl p-4 mb-6 text-left">
                      <div className="flex justify-between mb-2">
                        <span className="text-muted-foreground">Amount Paid</span>
                        <span className="font-bold text-secondary">
                          ${(paymentDetails.amount_total / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Order Status</span>
                        <span className="font-bold text-green-500 capitalize">
                          {paymentDetails.payment_status}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/shop" className="flex-1">
                      <Button className="w-full btn-outline" data-testid="continue-shopping">
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Continue Shopping
                      </Button>
                    </Link>
                    <Link to="/" className="flex-1">
                      <Button className="w-full btn-primary" data-testid="go-home">
                        <Home className="w-4 h-4 mr-2" />
                        Go Home
                      </Button>
                    </Link>
                  </div>
                </>
              )}

              {status === 'pending' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-accent" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Payment Processing
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    Your payment is still being processed. Please check your email for confirmation.
                  </p>
                  <Link to="/">
                    <Button className="btn-primary">
                      <Home className="w-4 h-4 mr-2" />
                      Return Home
                    </Button>
                  </Link>
                </>
              )}

              {(status === 'error' || status === 'expired') && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/20 flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-destructive" />
                  </div>
                  <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
                  </h1>
                  <p className="text-muted-foreground mb-6">
                    {status === 'expired' 
                      ? 'Your payment session has expired. Please try again.'
                      : 'We couldn\'t verify your payment. Please contact support if you were charged.'}
                  </p>
                  <Link to="/shop">
                    <Button className="btn-primary">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Return to Shop
                    </Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
